import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Col, Input, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import { QrReader, ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, dateHelpers } from '../utils';

export default function ConeLookup() {
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const [data, setData] = useState("");
   const [id, setConeId] = useState(0);
   const [model, setModel] = useState({});
   const [manualEntry, setManual] = useState(null);
   const [openQr, setOpenQr] = useState(false);
   const [spin, setSpinner] = useState(false);

   useEffect(() => {
      inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (data && data.length > 0) {
         // A scan was completed
         const values = data.split(",");
         if (Array.isArray(values)) {
            // Test that the user scanned a cone label - the item number should NOT be numeric.
            if (values.length === 2 && isNaN(+values[0]))
               runQuery(values[1]);
            else
               toast.error("Read error: invalid cone label");
         }
         else {
            setData("");
            toast.error("Read error: invalid cone label");
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data])

   useEffect(() => {
      inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id])

   useEffect(() => {
      if (manualEntry && id > 0)
         runQuery(id);
      else
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   const handleKeypress = e => {
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(1);
         return;
      }
      inputFocus("manualInput");
   };

   function handleQrScan(val) {
      setOpenQr(false);  // Hide QR Scanner
      setData(val);
   }

   function handleQrScanFailure(val) {
      setOpenQr(false);  // Hide QR Scanner
      toast.warning("This device does not have a camera.");
   }

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function runQuery(coneId) {
      if (coneId && coneId > 0) {
         setSpinner(true);
         setData("");
         setConeId(0);
         setManual(null);
         setModel({});
         let lookup = true;
         api
            .fetch(`Production/GetProductionRecordByConeId/${coneId}/${lookup}`)
            .then(r => {
               if (r.data.success) {
                  if (r.data.message !== null && r.data.message !== undefined) {
                     setModel(r.data.message);
                  } else {
                     toast.error("No data returned!");
                  }
               } else {
                  toast.error(r.data.message);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
            })
            .finally(() => {
               setSpinner(false);
               inputFocus("manualInput");
            })
      }
      else {
         toast.error("Read error: not a valid cone label");
      }
   }

   return (
      <DesktopContainer screenName="Cone Label Lookup">
         <ServiceThreadSpinner on={spin}>
            <div className="filter-header">
               <Row className="justify-content-center">
                  <Col xs={2}></Col>
                  <Col xs={8}>
                     <Table className="transparent-background my-0">
                        <tbody>
                           <tr>
                              <td className="transparent-background data-cell noWrap fw-bold h5 width70px p-0">
                                 Cone:
                              </td>
                              <td className="transparent-background data-cell noWrap h6 width115px">
                                 <Input
                                    type="number"
                                    name="manualInput"
                                    placeholder="Cone #"
                                    className="no-spinner"
                                    onChange={(e) => setConeId(e.target.value)}
                                    onKeyPress={handleKeypress}
                                 />
                              </td>
                              <td className="transparent-background data-cell noWrap h6 width115px">
                                 <Button className="shredButton btn-md my-3" onClick={() => setManual(1)}>
                                    Retrieve
                                 </Button>
                              </td>
                              <td className="transparent-background data-cell noWrap h6">
                                 <Button className="shredButton btn-md my-3 buttonRight" onClick={() => setOpenQr(!openQr)}>
                                    {openQr ? "Hide Scanner" : "Scan QR Code with Camera or webcam"}
                                 </Button>
                              </td>
                           </tr>
                        </tbody>
                     </Table>
                  </Col>
                  <Col xs={2}></Col>
               </Row>

               {openQr && <QrReader callback={handleQrScan} callbackError={handleQrScanFailure} />}
               <br />

               {model && model.item?.length > 0 ? ( // We have a production record
                  <Row className="justify-content-center">
                     <Col xs={2}></Col>
                     <Col xs={8}>
                        <Card className="dashboard-card" style={{ minWidth: '460px' }}>
                           <CardBody>
                              <div><span className="h2 me-2">Cone Information</span></div>
                              <hr />
                              <Table className="transparent-background my-0">
                                 <tbody>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Cone #:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.coneId}
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Item:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.itemWithDesc}
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Machine:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.machineDisplay}
                                       </td>
                                    </tr>
                                    {model && model.pkgWeight > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Package Weight:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.pkgWeight > 0 ? model.pkgWeight : ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Status:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.defect || "In Process"}
                                       </td>
                                    </tr>
                                    {model && model.dateCompleted ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Completed On:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {dateHelpers.dateTimeFormat(model.dateCompleted, dateHelpers.YMDHM) || ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.userName?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Completed By:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.userName || ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                    {model && model.shiftDesc?.length > 0 ? (
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          During:
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6">
                                          {model.shiftDesc || ""}
                                       </td>
                                    </tr>
                                    ) : null}
                                 </tbody>
                              </Table>
                           </CardBody>
                        </Card>
                     </Col>
                     <Col xs={2}></Col>
                  </Row>
               ) : null}
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer >
   );
}
